import React, { useState } from 'react'
import { Row, Col, Form, Button } from 'react-bootstrap'
import { OrderItemStatus } from '../../../config/enums'

interface Filter {
  field: string
  value: string
}

enum FilterField {
  status = 'Order status',
  customer = 'Customer',
  orderNumber = 'Order Number'
}

enum FilterValue {
  any = 'ANY'
}

export interface OrderFiltersProps {
  onFiltersChanged: (filters: {
    status?: OrderItemStatus
    orderNumber?: string
    customer?: string
  }) => void
  onFiltersCleared: () => void
  isLoading: boolean
}

const OrderFilters: React.FC<OrderFiltersProps> = ({
  onFiltersChanged,
  onFiltersCleared,
  isLoading
}) => {
  const [filter, setFilter] = useState<Filter>(() => {
    return { field: FilterField.status, value: FilterValue.any }
  })
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const statuses = [
    OrderItemStatus.processing,
    OrderItemStatus.disptached,
    OrderItemStatus.delivered
  ]

  const applyFilters = () => {
    setIsFilterApplied(true)

    if (filter.field === FilterField.status) {
      if (filter.value !== FilterValue.any) {
        onFiltersChanged({
          status: filter.value as OrderItemStatus
        })
      } else {
        onFiltersChanged({})
      }
    }
    if (filter.field === FilterField.customer) {
      onFiltersChanged({
        customer: filter.value
      })
    } else if (filter.field === FilterField.orderNumber) {
      onFiltersChanged({
        orderNumber: filter.value
      })
    }
  }

  const clearFilters = () => {
    setFilter({
      ...filter,
      value: ''
    })
    setIsFilterApplied(false)
    onFiltersCleared()
  }

  return (
    <Row>
      <Col xs="auto">
        <div>Filter by:</div>
        <Form.Control
          as="select"
          onChange={e =>
            setFilter({
              field: e.currentTarget.value,
              value:
                e.currentTarget.value === FilterField.status
                  ? String(FilterValue.any)
                  : ''
            })
          }
          disabled={isLoading}
          data-testid="filterBySelect"
        >
          <option>{FilterField.status}</option>
          <option>{FilterField.orderNumber}</option>
          <option>{FilterField.customer}</option>
        </Form.Control>
      </Col>
      <Col xs="auto">
        {filter.field === FilterField.status && (
          <>
            <div>Select status:</div>
            <Form.Control
              as="select"
              onChange={e =>
                setFilter({ ...filter, value: e.currentTarget.value })
              }
              value={
                filter.field === FilterField.status
                  ? filter.value
                  : FilterValue.any
              }
              disabled={isLoading}
              data-testid="statusSelect"
            >
              <option data-testid="statusOption">ANY</option>
              {statuses.map(status => {
                return (
                  <option key={status} data-testid="statusOption">
                    {status}
                  </option>
                )
              })}
            </Form.Control>
          </>
        )}
        {filter.field === FilterField.orderNumber && (
          <>
            <div>Search order number:</div>
            <Form.Control
              value={filter.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilter({ ...filter, value: e.target.value })
              }
              disabled={isLoading}
              data-testid="searchOrderNumber"
            ></Form.Control>
          </>
        )}
        {filter.field === FilterField.customer && (
          <>
            <div>Search customer:</div>
            <Form.Control
              value={filter.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFilter({ ...filter, value: e.target.value })
              }}
              disabled={isLoading}
              data-testid="searchCustomer"
            ></Form.Control>
          </>
        )}
      </Col>
      <Col xs="auto">
        <div>&nbsp;</div>
        <Button
          onClick={applyFilters}
          disabled={isLoading}
          data-testid="applyButton"
        >
          Apply
        </Button>
        {isFilterApplied && (
          <Button
            variant="danger"
            onClick={clearFilters}
            className="ml-3"
            disabled={isLoading}
            data-testid="clearButton"
          >
            Clear
          </Button>
        )}
      </Col>
      <Col></Col>
    </Row>
  )
}

export default OrderFilters
